import React, { useState } from 'react';
import { EnhancedDisplayType } from '@components/widgets/dynamic-display/EnhancedDisplay';
import { IDynamicDiplayView } from '@components/widgets/dynamic-display';
import DynamicWidget from '@components/widgets/dynamic-display/DynamicWidget';
import FormattingService from '@services/FormattingService';
import EnhancedButton from '@components/button/EnhancedButton';
import CancellationDetailsDrawer from '@modules/production/policy/page/tabs/amendments-tab/cancellation-details-drawer/CancellationDetailsDrawer';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { IAbstractRecord } from '@models';
import { IAmendmentGeneralData } from '../line-amendment';
import { IConfirmation } from '@redux/confirmation/types';
import { OpenConfirmationAction } from '@redux/confirmation/actions';
import { makeStyles } from 'tss-react/mui';

interface ICancellationDetailsWidgetProps {
  lovs: Record<string, Record<string, string>>;
  data: IAbstractRecord;
  generalData: IAmendmentGeneralData;
  allowEndorsement: boolean;
  onDataRefresh?: () => void;
}

const useStyles = makeStyles()(() => ({
  buttonContainer: {
    display: 'flex',
    gap: '10px',
  },
}));

const CancellationDetails: React.FC<ICancellationDetailsWidgetProps> = ({
  lovs,
  data,
  generalData,
  allowEndorsement,
  onDataRefresh,
}) => {
  const { classes } = useStyles();

  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();

  const [cancellationDrawerOpen, setCancellationDrawerOpen] =
    useState<boolean>(false);
  const [recalculate, setRecalculate] = useState<boolean>(false);

  const dynamicView: IDynamicDiplayView = {
    name: 'AmendmentDetails',
    sections: [
      {
        name: 'AmendmentDetails',
        title: 'Amendment Details',
        properties: {
          cancellationIssuanceDate: {
            multiline: false,
            value: data.production_Amendment_createdOn,
            name: 'cancellationIssuanceDate',
            title: 'Cancellation Issuance Date',
            type: EnhancedDisplayType.Date,
          },
          cancellationEffectiveDate: {
            multiline: false,
            value: data.production_Amendment_AmendmentEffectiveDate,
            name: 'cancellationEffectiveDate',
            title: 'Cancellation Effective Date',
            type: EnhancedDisplayType.Date,
          },
          cancellationReason: {
            multiline: false,
            value: FormattingService.formatCamelCaseToDisplay(
              data.production_Amendment_AmendmentReason
            ),
            name: 'cancellationReason',
            title: 'Cancellation Reason',
          },
        },
      },
    ],
  };

  return (
    <>
      <DynamicWidget
        hasFourFields={true}
        view={dynamicView}
        actions={
          allowEndorsement && (
            <div className={classes.buttonContainer}>
              <EnhancedButton
                isPrimary
                onClick={() => setCancellationDrawerOpen(true)}
                disabled={['COMPLETED', 'CANCELLED'].includes(
                  generalData.amendment.status
                )}
              >
                Modify
              </EnhancedButton>
              <EnhancedButton
                isPrimary
                disabled={['COMPLETED', 'CANCELLED'].includes(
                  generalData.amendment.status
                )}
                onClick={() => {
                  const confirmation: IConfirmation = {
                    open: true,
                    title: 'Confirm Recalculation',
                    message:
                      'Are you sure you want to modify and recalculate the overridden values? ',
                    callback: async () => {
                      setCancellationDrawerOpen(true);
                      setRecalculate(true);
                    },
                    submitButtonText: 'Confirm',
                    cancelButtonText: 'Cancel',
                  };
                  dispatch(OpenConfirmationAction(confirmation));
                }}
              >
                Modify & Recalculate
              </EnhancedButton>
            </div>
          )
        }
      />
      {cancellationDrawerOpen && (
        <CancellationDetailsDrawer
          open={cancellationDrawerOpen}
          onClose={() => setCancellationDrawerOpen(false)}
          onSuccess={() => {
            onDataRefresh();
          }}
          data={data}
          lovs={lovs}
          access={user.info?.businessUser_Access?.Title?.toUpperCase()}
          recalculate={recalculate}
        />
      )}
    </>
  );
};

export default CancellationDetails;
